import { lazy, Suspense, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AttributeType, ValueDataType } from '@sleed/bizcommerce-api';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Field } from '~/components/promotion/editor-section';
import { ImageUploader, type UploadedImage } from '@/components/image-uploader';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useAttributeMutations } from '~/hooks/use-attributes';
import { useAttributeCategoriesList } from '~/hooks/use-attribute-categories';
import { attributeSchema, EMPTY_ATTRIBUTE, type AttributeFormValues } from '~/lib/schemas/attribute';

/** Adapt a persisted image URL to the shape ImageUploader renders (no File ⇒ an existing server image). */
function urlToImage(url: string | undefined): UploadedImage | null {
	if (!url) return null;
	return { url, name: url.split('/').pop() || url, width: 0, height: 0, size: 0 };
}

const TipTapEditor = lazy(() => import('~/components/product/tiptap-editor').then(m => ({ default: m.TipTapEditor })));

const NO_CATEGORY = '__none__';
const ATTRIBUTE_TYPES = [AttributeType.SingleValue, AttributeType.ListOfValues];
const VALUE_DATA_TYPES = [ValueDataType.Text, ValueDataType.Integer, ValueDataType.Decimal, ValueDataType.Boolean, ValueDataType.DateTime];

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: AttributeFormValues | null;
}

/** Add / edit an attribute (technical characteristic) in a right-side sheet. */
export function AttributeSheet({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { create, update } = useAttributeMutations();
	const { data: categories = [] } = useAttributeCategoriesList();
	const form = useForm<AttributeFormValues>({
		resolver: zodResolver(attributeSchema) as Resolver<AttributeFormValues>,
		defaultValues: initial ?? EMPTY_ATTRIBUTE,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, control, formState } = form;
	const isEdit = Boolean(initial?.id);

	// Image slots are handled out-of-band (binaryRepoImagesApi upload + imgdel flags), not via RHF.
	const [img1, setImg1] = useState<UploadedImage | null>(null);
	const [img2, setImg2] = useState<UploadedImage | null>(null);

	useEffect(() => {
		if (open) {
			reset(initial ?? EMPTY_ATTRIBUTE);
			setImg1(urlToImage(initial?.img1));
			setImg2(urlToImage(initial?.img2));
		}
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		const images = {
			slot1: img1?.file ?? null,
			slot2: img2?.file ?? null,
			delete1: Boolean(initial?.img1) && !img1,
			delete2: Boolean(initial?.img2) && !img2
		};
		try {
			if (values.id) await update.mutateAsync({ form: values, images });
			else await create.mutateAsync({ form: values, images });
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const tErr = (message?: string) => (message ? t(message, { defaultValue: message }) : undefined);

	const toggleField = (name: 'iv' | 'daf' | 'imd' | 'isrc' | 'iiv', label: string) => (
		<div className="flex items-center justify-between rounded-md border border-border px-4 py-2">
			<Label htmlFor={`attr-${name}`}>{label}</Label>
			<Controller
				control={control}
				name={name}
				render={({ field }) => <Switch id={`attr-${name}`} checked={Boolean(field.value)} onCheckedChange={field.onChange} />}
			/>
		</div>
	);

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
				<SheetHeader className="border-b border-border px-5 py-4">
					<SheetTitle>{isEdit ? t('attributes.list.editTitle') : t('attributes.list.addTitle')}</SheetTitle>
					<SheetDescription>{t('attributes.list.formSubtitle')}</SheetDescription>
				</SheetHeader>

				<form onSubmit={onSubmit} noValidate className="flex min-h-0 flex-1 flex-col">
					<div className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
						<Field label={t('attributes.list.columns.name')} error={tErr(formState.errors.nm?.message)}>
							<Input {...register('nm')} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
						</Field>
						<div className="grid grid-cols-2 gap-4">
							<Field label={t('attributes.list.columns.code')} error={tErr(formState.errors.cd?.message)}>
								<Input {...register('cd')} maxLength={50} />
							</Field>
							<Field label={t('attributes.list.displayName')} error={tErr(formState.errors.dnm?.message)}>
								<Input {...register('dnm')} maxLength={250} />
							</Field>
						</div>

						<Field label={t('attributes.list.columns.category')}>
							<Controller
								control={control}
								name="acid"
								render={({ field }) => (
									<Select
										value={field.value ? String(field.value) : NO_CATEGORY}
										onValueChange={v => field.onChange(v === NO_CATEGORY ? null : Number(v))}>
										<SelectTrigger className="w-full">
											<SelectValue placeholder={t('attributes.list.categoryPlaceholder')} />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value={NO_CATEGORY}>{t('attributes.list.noCategory')}</SelectItem>
											{categories.map(c => (
												<SelectItem key={c.id} value={String(c.id)}>
													{c.nm}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								)}
							/>
						</Field>

						<div className="grid grid-cols-2 gap-4">
							<Field label={t('attributes.list.columns.type')} error={tErr(formState.errors.at?.message)}>
								<Controller
									control={control}
									name="at"
									render={({ field }) => (
										<Select value={String(field.value)} onValueChange={v => field.onChange(Number(v))}>
											<SelectTrigger className="w-full">
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{ATTRIBUTE_TYPES.map(v => (
													<SelectItem key={v} value={String(v)}>
														{t(`attributes.types.${v}`)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									)}
								/>
							</Field>
							<Field label={t('attributes.list.columns.valueDataType')} error={tErr(formState.errors.vt?.message)}>
								<Controller
									control={control}
									name="vt"
									render={({ field }) => (
										<Select value={String(field.value)} onValueChange={v => field.onChange(Number(v))}>
											<SelectTrigger className="w-full">
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{VALUE_DATA_TYPES.map(v => (
													<SelectItem key={v} value={String(v)}>
														{t(`attributes.valueDataTypes.${v}`)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									)}
								/>
							</Field>
						</div>

						<div className="grid grid-cols-2 gap-4">
							<Field
								label={t('attributes.list.valueFormat')}
								error={tErr(formState.errors.vfs?.message)}
								help={t('attributes.list.valueFormatHelp')}>
								<Input {...register('vfs')} maxLength={50} />
							</Field>
							<Field label={t('attributes.list.columns.sort')} error={tErr(formState.errors.si?.message)}>
								<Input type="number" {...register('si', { valueAsNumber: true })} />
							</Field>
						</div>

						<Field label={t('attributes.list.erpCode')} error={tErr(formState.errors.erpcd?.message)}>
							<Input {...register('erpcd')} maxLength={50} />
						</Field>

						<div className="grid grid-cols-2 gap-3">
							{toggleField('iv', t('attributes.list.columns.visible'))}
							{toggleField('daf', t('attributes.list.disableFromFilters'))}
							{toggleField('imd', t('attributes.list.mandatory'))}
							{toggleField('isrc', t('attributes.list.searchable'))}
							{toggleField('iiv', t('attributes.list.infoVisible'))}
						</div>

						<Field label={t('attributes.list.infoText')} help={t('attributes.list.infoTextHelp')}>
							<Controller
								control={control}
								name="itxt"
								render={({ field }) => (
									<Suspense fallback={<Skeleton className="h-40 w-full rounded-md" />}>
										<TipTapEditor value={field.value ?? ''} onChange={html => field.onChange(html)} allowMedia minHeight={160} />
									</Suspense>
								)}
							/>
						</Field>

						<div className="grid grid-cols-1 gap-4 rounded-md border border-border-subtle p-4 sm:grid-cols-2">
							<Field label={`${t('attributes.list.image')} (1)`}>
								<ImageUploader value={img1} onChange={setImg1} helpText={t('attributes.list.imageHelp')} />
							</Field>
							<Field label={`${t('attributes.list.image')} (2)`}>
								<ImageUploader value={img2} onChange={setImg2} helpText={t('attributes.list.imageHelp')} />
							</Field>
						</div>
					</div>

					<SheetFooter className="border-t border-border px-5 py-4">
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</SheetFooter>
				</form>
			</SheetContent>
		</Sheet>
	);
}
